import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsDatabaseFillAdd,
  BsDatabaseFillDash,
  BsEye,
  BsPencilSquare,
  BsPlusLg,
  BsSearch,
  BsTrash,
  BsX,
  BsBoxSeam,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";
import ActivityLog from "../activity/ActivityLog";

import "./inventorydashboard.css";
import {
  serviceGetAllProducts,
  serviceUpdateProductQuantity,
  serviceDeleteProduct,
} from "../../api/serviceApi";

import { serviceDeletePhoto } from "../../api/servicePhotoApi";

import { getImageUrl } from "../../utils/helper";

const ITEMS_PER_PAGE = 20;

// ============================================================
// HELPERS
// ============================================================

const getId = (item) => item?.id || item?._id || "";

const getQuantity = (product) => {
  const value = Number(product?.quantity);

  return Number.isFinite(value) ? value : 0;
};

// ============================================================
// COMPONENT
// ============================================================
const InventoryDashboard = () => {
  const searchRef = useRef(null);

  const [productArray, setProductArray] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // ==========================================================
  // LOAD PRODUCTS
  // ==========================================================
  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      try {
        setLoading(true);

        const result = await serviceGetAllProducts();

        if (cancelled) {
          return;
        }

        setProductArray(Array.isArray(result) ? result : result?.data || []);
      } catch (error) {
        console.error("Error fetching products: ", error);
        if (!cancelled) {
          toast.error(error?.message || "Failed to load products");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================================
  // SEARCH
  // ==========================================================
  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return productArray;
    }

    return productArray.filter((product) => {
      const haystack = [
        product?.mpn,
        product?.name,
        product?.supplier,
        product?.type,
        product?.shelfAddress,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [productArray, searchQuery]);

  // ==========================================================
  // PAGINATION
  // ==========================================================
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / ITEMS_PER_PAGE),
  );

  const currentProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ==========================================================
  // CLEAR SEARCH
  // ==========================================================
  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    searchRef.current?.focus();
  };

  // ==========================================================
  // QUANTITY STATE
  // ==========================================================
  const getQuantityState = (item) => {
    const quantity = Number(item?.quantity) || 0;

    const minimumValue = Number(item?.minQuantity);

    const maximumValue = Number(item?.maxQuantity);

    const minimum = Number.isFinite(minimumValue) ? minimumValue : 5;

    const maximum = Number.isFinite(maximumValue) ? maximumValue : 30;

    if (quantity < minimum) {
      return "low";
    }

    if (maximum > 0 && quantity > maximum) {
      return "high";
    }

    return "normal";
  };

  // ==========================================================
  // ADD / SUBTRACT STOCK
  // ==========================================================

  const updateStock = async (product, type) => {
    const productId = getId(product);

    if (!productId) {
      toast.error("Product ID is missing.");
      return;
    }

    const action = type === "add" ? "add" : "subtract";

    const userInput = window.prompt(`How many items do you want to ${action}?`);

    if (userInput === null) {
      return;
    }

    const amount = Number(userInput);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Please enter a valid quantity greater than 0.");

      return;
    }

    const currentQuantity = getQuantity(product);

    if (type === "subtract" && amount > currentQuantity) {
      toast.error(
        `You only have ${currentQuantity} item${
          currentQuantity === 1 ? "" : "s"
        } in stock.`,
      );

      return;
    }

    const newQuantity =
      type === "add" ? currentQuantity + amount : currentQuantity - amount;

    try {
      await serviceUpdateProductQuantity({
        id: productId,
        quantity: newQuantity,
      });

      setProductArray((current) =>
        current.map((item) =>
          getId(item) === productId
            ? {
                ...item,
                quantity: newQuantity,
              }
            : item,
        ),
      );

      ActivityLog(
        "Product Qty Updated",
        `${productId} stock ${
          type === "add" ? "increased" : "decreased"
        } by ${amount}`,
      );

      toast.success(
        `Stock ${type === "add" ? "increased" : "decreased"} by ${amount}.`,
      );
    } catch (error) {
      console.error("Error updating product quantity:", error);

      toast.error(error?.message || "Failed to update product quantity.");
    }
  };

  // ==========================================================
  // DELETE PRODUCT
  // ==========================================================
  const onDelete = async (item) => {
    const productId = getId(item);

    if (!productId) {
      toast.error("Product ID is missing.");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${
        item.name || item.mpn || "this product"
      }? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      // ----------------------------------------
      // DELETE PRODUCT
      // ----------------------------------------

      await serviceDeleteProduct(productId);

      // ----------------------------------------
      // REMOVE FROM LOCAL UI
      // ----------------------------------------

      setProductArray((current) =>
        current.filter((product) => getId(product) !== productId),
      );

      // ----------------------------------------
      // CLEAN UP PHOTO AFTER PRODUCT DELETE
      // ----------------------------------------

      if (item.imageUrl) {
        try {
          await serviceDeletePhoto(item.imageUrl);
        } catch (photoError) {
          console.error(
            "Product deleted but photo cleanup failed:",
            photoError,
          );
        }
      }

      toast.success("Product deleted successfully.");
    } catch (error) {
      console.error("Error deleting product:", error);

      toast.error(error?.message || "Failed to delete product.");
    }
  };

  return (
    <div className="product-page">
      <Sidebar />

      <main className="product-content">
        <div className="product-container">
          {/* ========== HEADER ========== */}
          <header className="product-header">
            <div>
              <h1>Inventory</h1>
              <p>Manage products, stock levels and inventory locations.</p>
            </div>

            <Link to="/inventory/new" className="product-btn primary">
              <BsPlusLg />
              <span>Add Product</span>
            </Link>
          </header>

          {/* ========== SEARCH ========== */}
          <section className="product-toolbar">
            <div className="product-search">
              <label htmlFor="product-search">Search</label>

              <div className="product-search-control">
                <BsSearch className="product-search-icon" />

                <input
                  ref={searchRef}
                  id="product-search"
                  type="text"
                  placeholder="Stock code, name, type, supplier..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />

                {searchQuery && (
                  <button
                    type="button"
                    className="product-search-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <BsX />
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ========== TABLE ========== */}
          <section className="product-table-card">
            <div className="product-table-meta">
              <span>
                Showing <strong>{currentProducts.length}</strong> of{" "}
                <strong>{filteredProducts.length}</strong> products
              </span>

              {searchQuery && (
                <span className="product-searching">
                  Searching for “{searchQuery}”
                </span>
              )}
            </div>

            <div className="product-table-scroll">
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th className="hide-tablet">MPN</th>
                    <th className="hide-tablet">Shelf</th>
                    <th className="hide-mobile">Supplier</th>
                    <th className="hide-mobile">Type</th>
                    <th className="product-actions-heading">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {/* ========== LOADING ========== */}
                  {loading ? (
                    <tr>
                      <td colSpan="7">
                        <div className="product-empty">
                          <strong>Loading products...</strong>
                        </div>
                      </td>
                    </tr>
                  ) : currentProducts.length === 0 ? (
                    <tr>
                      {/* ========== EMPTY ========== */}
                      <td colSpan="7">
                        <div className="product-empty">
                          <BsSearch />
                          <strong>No products found</strong>
                          <span>Try changing your search.</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentProducts.map((item) => {
                      const quantityState = getQuantityState(item);
                      const imageUrl = getImageUrl(item.imageUrl);

                      return (
                        <tr key={item.id}>
                          {/* ========== PRODUCT ========== */}
                          <td>
                            <div className="product-item">
                              <button
                                type="button"
                                className="product-image-button"
                                onClick={() =>
                                  imageUrl && setEnlargedImageUrl(imageUrl)
                                }
                                disabled={!imageUrl}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={item.name || "Product"}
                                    className="product-image"
                                  />
                                ) : (
                                  <BsBoxSeam className="product-image-placeholder-icon" />
                                )}
                              </button>

                              <div className="product-item-details">
                                <strong>
                                  {item.name || "Unnamed Product"}
                                </strong>

                                <span className="product-mobile-code">
                                  {item.mpn || "-"}
                                </span>
                              </div>
                            </div>
                          </td>

                          <td>
                            <span
                              className={`quantity-badge quantity-${quantityState}`}
                            >
                              {item.quantity ?? 0}
                            </span>
                          </td>

                          <td className="hide-tablet">
                            <span className="product-code">
                              {item.mpn || "—"}
                            </span>
                          </td>

                          <td className="hide-tablet">
                            {item.shelfAddress || "—"}
                          </td>

                          <td className="hide-mobile">
                            {item.supplier || "—"}
                          </td>

                          <td className="hide-mobile">{item.type || "—"}</td>

                          {/* ========== ACTIONS ========== */}
                          <td className="product-table-actions">
                            <Link
                              to={`/inventory/view/${item.id}`}
                              className="product-action view"
                              aria-label="View product"
                              title="View product"
                            >
                              <BsEye />
                            </Link>

                            <Link
                              to={`/inventory/update/${item.id}`}
                              className="product-action edit"
                              aria-label="Edit product"
                              title="Edit product"
                            >
                              <BsPencilSquare />
                            </Link>

                            <button
                              type="button"
                              className="product-action add"
                              onClick={() => updateStock(item, "add")}
                              aria-label="Add stock"
                              title="Add stock"
                            >
                              <BsDatabaseFillAdd />
                            </button>

                            <button
                              type="button"
                              className="product-action subtract"
                              onClick={() => updateStock(item, "subtract")}
                              aria-label="Subtract stock"
                              title="Subtract stock"
                            >
                              <BsDatabaseFillDash />
                            </button>

                            <button
                              type="button"
                              className="product-action delete"
                              onClick={() => onDelete(item)}
                              aria-label="Delete product"
                              title="Delete product"
                            >
                              <BsTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ========== PAGINATION ========== */}
            {totalPages > 1 && (
              <div className="product-pagination">
                <span>
                  Page <strong>{currentPage}</strong> of{" "}
                  <strong>{totalPages}</strong>
                </span>

                <div className="product-pagination-buttons">
                  <button
                    type="button"
                    className="product-btn ghost"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((previous) => Math.max(previous - 1, 1))
                    }
                  >
                    Previous
                  </button>

                  <button
                    type="button"
                    className="product-btn ghost"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((previous) =>
                        Math.min(previous + 1, totalPages),
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>

      {/* ========== ENLARGED IMAGE ========== */}
      {enlargedImageUrl && (
        <div
          className="product-image-overlay"
          role="presentation"
          onClick={() => setEnlargedImageUrl(null)}
        >
          <button
            type="button"
            className="product-image-close"
            onClick={() => setEnlargedImageUrl(null)}
            aria-label="Close image"
          >
            <BsX />
          </button>

          <img
            src={enlargedImageUrl}
            alt="Enlarged product"
            className="product-image-enlarged"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default InventoryDashboard;
